import type {
  AgenticLogicActiveRepairRun,
  AgenticLogicPublishGateResultDto,
} from "@intelligems/ig-types";

/** One settled, refused attempt, in the shape the gate rail renders. */
export interface PriorRepairAttempt {
  attempt: number;
  gates: AgenticLogicPublishGateResultDto[];
}

/**
 * The refused attempts the gate rail should still name.
 *
 * The rail follows the repair loop's CURRENT publish job, so an attempt the
 * loop has moved past — or converged over — leaves the screen with the
 * pointer. These rows are the record that survives it.
 *
 * Two exclusions, both about not lying:
 *
 *  - The tracked job is the rail itself; repeating it as history would show
 *    one failure twice.
 *  - A FINISHED run whose attempts do not include the tracked job is history
 *    about some earlier publish. Beside the merchant's own later, unrelated
 *    publish those rows would read as its refusals, so the whole list drops.
 */
export const priorRepairAttempts = (
  run: AgenticLogicActiveRepairRun | null,
  trackedJobId: string | null | undefined
): PriorRepairAttempt[] => {
  if (run === null) {
    return [];
  }

  const describesTrackedJob =
    run.finishedAtTs === null ||
    run.attempts.some((attempt) => attempt.publishJobId === trackedJobId);
  if (!describesTrackedJob) {
    return [];
  }

  return run.attempts
    .filter(
      (attempt) =>
        attempt.status === "failed" && attempt.publishJobId !== trackedJobId
    )
    .map((attempt) => ({
      attempt: attempt.attempt,
      gates: attempt.failedGates,
    }));
};
